/**
 * API Error Handler Utility
 * Part of TASK-334: Frontend Admin Panel
 *
 * Type-safe error handling for API responses including:
 * - Axios error responses
 * - FastAPI validation errors
 * - Standard Error objects
 * - Unknown error types
 */

import type { AxiosError } from 'axios';

/**
 * Error codes for common API errors
 */
export const ApiErrorCodes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ApiErrorCode = (typeof ApiErrorCodes)[keyof typeof ApiErrorCodes];

/**
 * Parsed API error structure
 */
export interface ApiError {
  code: ApiErrorCode;
  message: string;
  statusCode?: number;
  details?: Record<string, unknown>;
}

/**
 * FastAPI validation error item structure
 */
interface FastAPIValidationError {
  loc?: (string | number)[];
  msg?: string;
  message?: string;
  type?: string;
}

/**
 * Type guard to check if an error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Type guard to check if error response has a detail field
 */
function hasDetailField(
  data: unknown
): data is { detail: string | FastAPIValidationError[] } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'detail' in data &&
    (typeof (data as { detail: unknown }).detail === 'string' ||
      Array.isArray((data as { detail: unknown }).detail))
  );
}

/**
 * Type guard to check if error response has a message field
 */
function hasMessageField(data: unknown): data is { message: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof (data as { message: unknown }).message === 'string'
  );
}

/**
 * Type guard to check if error response has an error field
 */
function hasErrorField(data: unknown): data is { error: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as { error: unknown }).error === 'string'
  );
}

/**
 * Extract message from FastAPI validation error array
 */
function extractValidationMessage(errors: FastAPIValidationError[]): string {
  if (errors.length === 0) return '';

  const firstError = errors[0];
  // Try to get 'msg' or 'message' property
  const message = firstError.msg || firstError.message;
  if (message) {
    // Include field location if available
    if (firstError.loc && firstError.loc.length > 0) {
      const field = firstError.loc.filter((l) => l !== 'body').join('.');
      if (field) {
        return `${field}: ${message}`;
      }
    }
    return message;
  }

  // Fallback to JSON representation
  return JSON.stringify(firstError);
}

/**
 * Get error code from HTTP status
 */
function getErrorCodeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case 401:
      return ApiErrorCodes.UNAUTHORIZED;
    case 403:
      return ApiErrorCodes.FORBIDDEN;
    case 404:
      return ApiErrorCodes.NOT_FOUND;
    case 409:
      return ApiErrorCodes.CONFLICT;
    case 422:
      return ApiErrorCodes.VALIDATION_ERROR;
    default:
      if (status >= 500) return ApiErrorCodes.SERVER_ERROR;
      return ApiErrorCodes.UNKNOWN;
  }
}

/**
 * Parse an error into a structured ApiError object
 *
 * @param error - The error to parse (can be any type)
 * @returns Parsed ApiError structure
 */
export function parseApiError(error: unknown): ApiError {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const response = error.response;

    // No response - network error
    if (!response) {
      return {
        code: ApiErrorCodes.NETWORK_ERROR,
        message: error.message || 'Network error - please check your connection',
      };
    }

    const { data, status } = response;
    const code = getErrorCodeFromStatus(status);

    // Try to extract message from response data
    let message = 'An unexpected error occurred';

    if (hasDetailField(data)) {
      if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (Array.isArray(data.detail)) {
        message = extractValidationMessage(data.detail);
      }
    } else if (hasMessageField(data)) {
      message = data.message;
    } else if (hasErrorField(data)) {
      message = data.error;
    } else if (typeof data === 'string') {
      message = data;
    }

    return {
      code,
      message,
      statusCode: status,
      details: typeof data === 'object' ? (data as Record<string, unknown>) : undefined,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      code: ApiErrorCodes.UNKNOWN,
      message: error.message,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      code: ApiErrorCodes.UNKNOWN,
      message: error,
    };
  }

  // Unknown error type
  return {
    code: ApiErrorCodes.UNKNOWN,
    message: 'An unexpected error occurred',
  };
}

/**
 * Extract a user-friendly error message from any error type
 *
 * @param error - The error to extract message from
 * @param defaultMessage - Optional default message if extraction fails
 * @returns Human-readable error message
 *
 * @example
 * ```ts
 * try {
 *   await api.createUser(data);
 * } catch (error) {
 *   toast.error(extractErrorMessage(error, 'Failed to create user'));
 * }
 * ```
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'An unexpected error occurred'
): string {
  const parsed = parseApiError(error);
  return parsed.message || defaultMessage;
}
